import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import { useState, useRef } from 'react';
import useStore, {
  selectCountryTheme,
  selectFavoriteCountryId,
  selectSetUserBatch,
  selectExampleBatch,
} from '@/hooks/client/useStore';
import useProductTrace from '@/hooks/server/useProductTrace';
import EditSquareIcon from '@mui/icons-material/EditSquare';
import { useNavigate } from '@tanstack/react-router';
import PrimaryButton from '@/common/PrimaryButton';
import NotFound from '@/common/NotFound';
import LoadingProduct from './LoadingProduct';
import { adobeSearchBtnProcessStart } from '@/adobe-analitycs/adobeAnaliticsEvents';
import PropTypes from 'prop-types';
import Backdrop from '@mui/material/Backdrop';
import ProductTour from '@/features/product-tour/ProductTour';

const css = {
  title: {
    display: 'block',
    fontFamily: 'BrandonGrotesque',
    color: '#fff',
    fontSize: '16px',
    height: '25px',
  },
  fieldWrapper: { width: '100%' },
  textField: (showNotFound, color) => ({
    '& .MuiOutlinedInput-root': {
      backgroundColor: '#fff',
      borderRadius: '4px',
      '& fieldset': {
        borderColor: showNotFound ? 'red' : color,
        borderRadius: '4px',
      },
      '&:hover fieldset': {
        borderColor: showNotFound ? 'red' : color,
      },
      '&.Mui-focused fieldset': {
        borderColor: showNotFound ? 'red' : color,
      },
    },
  }),
  editIcon: (color) => ({ color: color }),
  notFoundText: {
    color: 'white',
    backgroundColor: 'red',
    padding: '8px 12px',
    borderRadius: '4px',
    marginTop: '8px',
    fontSize: '0.9rem',
  },
  checkbox: {
    backgroundColor: '#fff',
    borderRadius: '4px',
    padding: '0px',
    margin: '0px',
    mr: '7px',
    color: '#ACACAC',
    '&.Mui-checked': {
      color: '#ACACAC',
    },
  },
  dataPolicy: {
    cursor: 'pointer',
    textDecoration: 'underline',
    color: 'white',
    fontSize: '13px',
  },
  button: {
    '&.Mui-disabled': {
      backgroundColor: '#ccc',
      color: '#666',
      opacity: 1,
    },
  },
  dataPolicyBox: { textAlign: 'left', ml: 2 },
  tourFocus: {
    position: 'relative',
    zIndex: 1301,
    borderRadius: 1,
    backgroundColor: 'white',
  },
};

const SearchProduct = ({ runTour, handleRunTour }) => {
  const navigate = useNavigate();
  const countryTheme = useStore(selectCountryTheme);
  const countryCode = useStore(selectFavoriteCountryId);
  const setUserBatch = useStore(selectSetUserBatch);
  const exampleBatch = useStore(selectExampleBatch);
  const [aceptaPolitica, setAceptaPolitica] = useState(false);
  const [inputCode, setInputCode] = useState('');
  const [notFound, setNotFound] = useState(false);
  const [wrongFormat, setWrongFormat] = useState(false);
  const inputRef = useRef(null);
  const checkboxRef = useRef(null);
  const [step, setStep] = useState(0);

  const { isLoading: isSearching, isError, refetch } = useProductTrace(inputCode.trim(), false);

  const validateUserInput = (inputCode) => {
    // remove unnecessary space
    const trimmed = inputCode.trim();
    if (!trimmed) return null;

    // Validate regex: starts with L/l
    const regex = /^[Ll]/;
    const isValid = exampleBatch.toUpperCase().startsWith('L') ? regex.test(trimmed) : true;

    if (!isValid) {
      setWrongFormat(true);
      return null;
    }

    // if all right
    setNotFound(false);
    setWrongFormat(false);
    return trimmed;
  };

  const handleReceivedData = (data, userInput = exampleBatch) => {
    if (data) {
      const batch = userInput.toUpperCase(); // batch to show for quick view
      setUserBatch(batch);
      adobeSearchBtnProcessStart(countryCode, batch); // adobe analitycs code
      navigate({ to: '/batch' });
    } else {
      setNotFound(true);
    }
  };

  const handleSearch = async () => {
    // regular user search
    const validatedInput = validateUserInput(inputCode);
    if (validatedInput) {
      const { data } = await refetch();
      handleReceivedData(data, validatedInput);
    }
  };

  const handleSearchDemo = () => {
    // search from demo using default code
    setInputCode(exampleBatch);
    setAceptaPolitica(true);
    setTimeout(async () => {
      const { data } = await refetch();
      handleReceivedData(data);
    }, 0);
  };

  const steps = [
    {
      ref: inputRef,
      content: (
        <Box>
          <Typography>
            Ingresa el número de lote que{' '}
            <span style={{ textDecoration: 'underline' }}>encuentras en los empaques</span> de
            nuestros productos, asegurate que tengan el formato {exampleBatch}. No tienes un
            código.{' '}
          </Typography>
          <Link sx={{ fontWeight: 'bold', fontSize: '12px' }} onClick={handleSearchDemo}>
            Click aquí para un demo.
          </Link>
        </Box>
      ),
    },
    {
      ref: checkboxRef,
      content: <Typography>Recuerda aceptar las políticas antes de continuar.</Typography>,
    },
  ];

  const currentStep = steps[step];

  const handleNext = () => {
    if (step + 1 < steps.length) {
      setStep(step + 1);
    } else {
      setStep(0);
      handleRunTour(false);
    }
  };

  const handlePrev = () => {
    setStep(step - 1);
  };

  // Only show error overlay if it's a real error (not 404/400)
  if (isError && !notFound) return <NotFound />;

  const showNotFound = notFound && !isSearching;

  return (
    <>
      {/* Botón de cierre tipo X en la esquina superior derecha */}
      {runTour && (
        <>
          <Backdrop open sx={{ zIndex: 1200, backgroundColor: 'rgba(0,0,0,0.6)' }} />
          <IconButton
            onClick={() => handleRunTour(false)}
            sx={{
              position: 'fixed',
              top: '15px',
              right: '15px',
              zIndex: 10000,
              color: 'white',
            }}
            aria-label="Cerrar tour"
          >
            <CloseIcon sx={{ fontSize: '26px' }} />
          </IconButton>
        </>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mx: 'auto' }}>
        <Typography sx={css.title}>Ingresa el código del lote</Typography>

        <Box sx={css.fieldWrapper}>
          <TextField
            inputRef={inputRef}
            id="search-input"
            variant="outlined"
            placeholder={`Ej: ${exampleBatch}`}
            onChange={(e) => {
              setInputCode(e.target.value);
              setNotFound(false);
              setWrongFormat(false);
            }}
            fullWidth
            error={showNotFound}
            sx={
              runTour && step === 0
                ? css.tourFocus
                : css.textField(showNotFound, countryTheme.secondaryColor)
            }
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton aria-label="Ingrese el lote">
                      <EditSquareIcon sx={css.editIcon(countryTheme.secondaryColor)} />
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
          />

          {isSearching && <LoadingProduct />}

          {/* ERROR MESSAGES */}
          {showNotFound && (
            <Typography sx={css.notFoundText}>
              No se encontró el lote con código "{inputCode.trim()}"
            </Typography>
          )}

          {wrongFormat && (
            <Typography sx={css.notFoundText}>
              El lote debe empezar con la letra 'L'. <strong>Ejemplo: {exampleBatch}</strong>
            </Typography>
          )}
          {/* END ERROR MESSAGES */}
        </Box>

        {/* Data policy */}
        <Box sx={css.dataPolicyBox}>
          <FormControlLabel
            id="data-policy-checkbox"
            control={
              <Checkbox
                checked={aceptaPolitica}
                onChange={(e) => setAceptaPolitica(e.target.checked)}
                sx={runTour && step === 1 ? { ...css.tourFocus, ...css.checkbox } : css.checkbox}
                slotProps={{ input: { ref: checkboxRef } }}
              />
            }
            label={
              <Link
                variant="body"
                href={`/docs/${countryCode}/data-policy.pdf`}
                rel="noopener noreferrer"
                sx={css.dataPolicy}
              >
                Acepto la política de tratamiento de datos
              </Link>
            }
          />
        </Box>

        {/* product tour */}
        {runTour && currentStep?.ref?.current && (
          <ProductTour
            anchorEl={currentStep.ref.current}
            step={step}
            stepsLength={steps.length}
            content={currentStep?.content}
            onNext={handleNext}
            onPrev={handlePrev}
          />
        )}

        {/* Search button */}
        <PrimaryButton
          text={'Conoce el origen aquí'}
          onClick={handleSearch}
          disabled={!aceptaPolitica || !inputCode.trim()}
          sx={css.button}
        />
      </Box>
    </>
  );
};

SearchProduct.propTypes = {
  runTour: PropTypes.bool.isRequired,
  handleRunTour: PropTypes.func.isRequired,
};

export default SearchProduct;
